import type { Fields, RootConfig } from '@puckeditor/core';
import type { ComponentAiParams } from '@puckeditor/plugin-ai';

import { PageRootRender } from './page-root-render';

/** Header/Navigation show-hide fields shared by every CMS editor's root config. */
const SHARED_ROOT_FIELDS: Fields<{ showHeader: boolean; showNavigation: boolean }> = {
  showHeader: {
    type: 'radio',
    label: 'Header',
    options: [
      { label: 'Show', value: true },
      { label: 'Hide', value: false },
    ],
    ai: { schema: { type: 'boolean' } },
  },
  showNavigation: {
    type: 'radio',
    label: 'Navigation',
    options: [
      { label: 'Show', value: true },
      { label: 'Hide', value: false },
    ],
    ai: { schema: { type: 'boolean' } },
  },
};

const SHARED_ROOT_DEFAULT_PROPS = {
  showHeader: true,
  showNavigation: true,
};

export type CreatePageRootConfigOptions = {
  /** Page-model-specific fields (e.g. standalone-page's `urlPath`), merged alongside the shared Header/Navigation fields. */
  fields?: Fields;
  /** Page-model-specific default prop values, merged alongside the shared Header/Navigation defaults. */
  defaultProps?: Record<string, unknown>;
  /** AI authoring instructions for this page model's root. Omit entirely for models that don't need AI support. */
  ai?: ComponentAiParams;
};

/**
 * Builds the root Puck config shared by both CMS editors (standalone page + menu landing
 * page): the Header/Navigation show/hide fields and defaults, and the shared PageRootRender
 * render function. Page-specific fields/defaultProps (e.g. standalone-page's `urlPath`) are
 * merged in ahead of the shared ones so they render first in the sidebar.
 */
export function createPageRootConfig({ fields, defaultProps, ai }: CreatePageRootConfigOptions = {}): RootConfig {
  return {
    fields: { ...fields, ...SHARED_ROOT_FIELDS },
    defaultProps: { ...defaultProps, ...SHARED_ROOT_DEFAULT_PROPS },
    ...(ai ? { ai } : {}),
    render: PageRootRender,
  };
}
